import { FileText } from "lucide-react";
import { useMemo } from "react";

import { useListTranscriptItemsForEncounter } from "@elation/api-client/src/stitch-web";

import { TranscriptItemEntity } from "@src/features/transcription";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";

import type { VisitTranscriptItemV2 } from "../../VisitTranscript";

const TRANSCRIPT_POLL_INTERVAL_MS = 3_000;

export interface CompletedVisitTranscriptPanelProps {
  encounterId?: number;
  items?: VisitTranscriptItemV2[];
  isLoading?: boolean;
  isError?: boolean;
}

export function CompletedVisitTranscriptPanel({
  encounterId,
  items: itemsProp,
  isLoading: isLoadingProp = false,
  isError: isErrorProp = false,
}: CompletedVisitTranscriptPanelProps) {
  const transcriptItemsQuery = useListTranscriptItemsForEncounter(
    encounterId ?? 0,
    {
      query: {
        enabled: encounterId != null && itemsProp == null,
        refetchInterval: TRANSCRIPT_POLL_INTERVAL_MS,
      },
    },
  );
  const items = useMemo<VisitTranscriptItemV2[]>(() => {
    if (itemsProp) return itemsProp;

    const sorted = [...(transcriptItemsQuery.data?.data ?? [])].sort(
      TranscriptItemEntity.compareByOffsetAsc,
    );
    const speakerLabelMap =
      TranscriptItemEntity.buildSessionSpeakerLabelMap(sorted);

    return sorted.map((item) => ({
      id: String(item.id),
      startOffsetMs: item.startOffsetMs,
      endOffsetMs: item.endOffsetMs,
      speaker: item.speaker,
      speakerLabel: TranscriptItemEntity.sessionSpeakerLabel(
        item.speaker,
        speakerLabelMap,
      ),
      state: item.state,
      text: item.text,
    }));
  }, [itemsProp, transcriptItemsQuery.data?.data]);

  const isInitialLoading =
    isLoadingProp ||
    (itemsProp == null && transcriptItemsQuery.isLoading && items.length === 0);
  const isError =
    isErrorProp || (itemsProp == null && transcriptItemsQuery.isError);
  const error = isErrorProp ? true : transcriptItemsQuery.error;

  return (
    <div className="px-[18px] py-4">
      <div className="mb-3 flex items-center gap-2">
        <FileText className="size-3.5 text-primary" aria-hidden />
        <h2 className="font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          Visit transcript · {items.length} lines
        </h2>
      </div>

      {isError && items.length === 0 ? (
        <ErrorMessage
          error={error}
          fallback="Unable to load transcript."
          sizing="xs"
        />
      ) : isInitialLoading ? (
        <p
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="text-[13px] text-muted-foreground"
        >
          Loading transcript...
        </p>
      ) : items.length > 0 ? (
        <ol className="space-y-2" aria-label="Completed visit transcript">
          {items.map((item) => (
            <li key={item.id} className="text-[12.5px] leading-relaxed">
              {item.speakerLabel ? (
                <span className="mr-1 font-semibold text-primary">
                  {item.speakerLabel}
                </span>
              ) : null}
              <span className="text-foreground">
                {item.text?.trim() ? item.text : "(no transcription)"}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p
          role="status"
          aria-live="polite"
          className="text-[13px] text-muted-foreground"
        >
          No transcript is available for this completed visit.
        </p>
      )}
    </div>
  );
}
